/**
 * Toggle Dependency System
 *
 * Declarative map of parent→child toggle dependencies.
 * When a parent condition is unmet, its children are auto-disabled with a tooltip reason.
 *
 * Cascade example: Standard mode → locks historySource to 'off' → locks all 3 efficiency metrics.
 */

import type { AnalysisConfig } from '@views/picks/constants';

// =============================================================================
// Types
// =============================================================================

export interface DependencyState {
  disabled: boolean;
  disabledReason: string | undefined;
}

interface DependencyRule {
  /** Dot-path to the child toggle (e.g., 'efficiencyMetrics.bettingValueEfficiency') */
  child: string;
  /** Human-readable reason shown as tooltip when disabled */
  reason: string;
  /** Returns true when the parent condition IS met (child should be enabled) */
  parentMet: (config: AnalysisConfig) => boolean;
}

// =============================================================================
// Dependency Rules
// =============================================================================

const DEPENDENCY_RULES: DependencyRule[] = [
  // Standard mode can't fit historical data analysis
  {
    child: 'otherFactors.historySource',
    reason: 'Requires Detailed or Premium mode',
    parentMet: (c) => (c.outputOptions?.analysisMode ?? 'standard') !== 'standard',
  },

  // Betting history metrics require historySource to be ON
  {
    child: 'efficiencyMetrics.bettingValueEfficiency',
    reason: 'Requires History Source to be enabled',
    parentMet: (c) => (c.otherFactors?.historySource ?? 'off') !== 'off',
  },
  {
    child: 'efficiencyMetrics.historicalTrends',
    reason: 'Requires History Source to be enabled',
    parentMet: (c) => (c.otherFactors?.historySource ?? 'off') !== 'off',
  },

  // trendAnalysis is auto-derived from 2+ stat windows — no dependency rule needed

  // Usage trend requires usage rate data
  {
    child: 'otherFactors.usageTrend',
    reason: 'Requires Usage Rate to be enabled',
    parentMet: (c) => c.advancedStats?.usageRate === true,
  },

  // Efficiency trend requires true shooting data
  {
    child: 'otherFactors.efficiencyTrend',
    reason: 'Requires True Shooting % to be enabled',
    parentMet: (c) => c.advancedStats?.trueShootingPct === true,
  },
];

// =============================================================================
// Utility: Resolve nested config values by dot-path
// =============================================================================

/**
 * Read a nested config value using a dot-path string.
 * @example resolveConfigValue(config, 'otherFactors.historySource') => 'off'
 */
export function resolveConfigValue(
  config: AnalysisConfig,
  dotPath: string,
): boolean | string | number | undefined {
  const parts = dotPath.split('.');
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  // biome-ignore lint/suspicious/noExplicitAny: Dynamic nested property access requires any
  let current: any = config;
  for (const part of parts) {
    if (current == null || typeof current !== 'object') return undefined;
    current = current[part];
  }
  return current as boolean | string | number | undefined;
}

// =============================================================================
// Core API
// =============================================================================

/**
 * Check if a child toggle should be disabled based on its parent dependencies.
 * A child is disabled if ANY of its parent conditions are unmet.
 */
export function getDependencyState(config: AnalysisConfig, childDotPath: string): DependencyState {
  for (const rule of DEPENDENCY_RULES) {
    if (rule.child === childDotPath && !rule.parentMet(config)) {
      return { disabled: true, disabledReason: rule.reason };
    }
  }
  return { disabled: false, disabledReason: undefined };
}

// Mapping of parent paths to their direct child dependencies
const PARENT_TO_CHILDREN: Record<string, string[]> = {
  'outputOptions.analysisMode': ['otherFactors.historySource'],
  'otherFactors.historySource': [
    'efficiencyMetrics.bettingValueEfficiency',
    'efficiencyMetrics.historicalTrends',
  ],
  'advancedStats.usageRate': ['otherFactors.usageTrend'],
  'advancedStats.trueShootingPct': ['otherFactors.efficiencyTrend'],
};

/**
 * Find all children that should be cascade-disabled when a parent changes.
 * Returns dot-paths of children whose parent conditions are no longer met.
 */
export function getChildrenToDisable(config: AnalysisConfig, changedParentPath: string): string[] {
  const directChildren = PARENT_TO_CHILDREN[changedParentPath] ?? [];
  const disabledChildren: string[] = [];

  for (const childPath of directChildren) {
    const state = getDependencyState(config, childPath);
    if (state.disabled) {
      disabledChildren.push(childPath);
      // Recurse: this child may also be a parent to others
      const grandChildren = getChildrenToDisable(config, childPath);
      disabledChildren.push(...grandChildren);
    }
  }

  return [...new Set(disabledChildren)];
}

/**
 * Apply cascade disabling to a config: for each disabled child, set its value to false/off.
 * Iteratively applies changes and re-checks so cascades propagate fully
 * (e.g., standard mode → historySource off → efficiency metrics off).
 */
export function applyCascadeDisable(
  config: AnalysisConfig,
  changedParentPath: string,
): AnalysisConfig {
  let updated = { ...config };
  const visited = new Set<string>();
  const queue = [changedParentPath];

  while (queue.length > 0) {
    const parentPath = queue.shift()!;
    if (visited.has(parentPath)) continue;
    visited.add(parentPath);

    // Find direct children whose conditions are now unmet in the CURRENT config
    const directChildren = PARENT_TO_CHILDREN[parentPath] ?? [];
    for (const childPath of directChildren) {
      const state = getDependencyState(updated, childPath);
      if (!state.disabled) continue;

      const [section, key] = childPath.split('.');
      const currentSection = updated[section];
      if (typeof currentSection !== 'object' || currentSection === null) continue;

      // historySource uses 'off' instead of false
      const disabledValue = key === 'historySource' ? 'off' : false;
      updated = {
        ...updated,
        [section]: {
          ...currentSection,
          [key]: disabledValue,
        },
      } as AnalysisConfig;

      // Queue this child as a potential parent for further cascade
      queue.push(childPath);
    }
  }

  return updated;
}

/**
 * Apply cascade disable across ALL parent paths.
 * Used on config load to normalize stale state from before the dependency system existed.
 */
export function applyCascadeDisableAll(config: AnalysisConfig): AnalysisConfig {
  let updated = config;
  for (const parentPath of Object.keys(PARENT_TO_CHILDREN)) {
    updated = applyCascadeDisable(updated, parentPath);
  }
  return updated;
}

// Export rules for testing
export const _DEPENDENCY_RULES = DEPENDENCY_RULES;
